import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {useForm} from '../../common/hooks/useForm'
import {useNotification} from '../../common/notifications/useNotification'
import {Routes} from '../../common/Routes'
import {ProductRentalType} from '../../common/types/product'
import {AllProductsDocument, useAddProductMutation} from '../../generated/graphql'
import {transformProductRentalTypeToGQL} from '../../gql/transformProduct'
import {Box} from '../../webui/Box'
import {SaveButton} from '../../webui/buttons/SaveButton'
import {SelectInputProps} from '../../webui/inputs/Select'
import {Switch, SwitchProps} from '../../webui/inputs/Switch'
import {TextField, TextFieldProps} from '../../webui/inputs/TextField'
import {Typography} from '../../webui/Typography'
import {Link} from '../../webui/Link'
import {useAllPriceWeights} from '../price/gql/useAllPriceWeights'
import {defaultPriceWeightId} from '../price/priceWeightConstants'
import {PriceWeightSelect} from '../price/PriceWeightSelect'
import {RentalTypeSelect} from './RentalTypeSelect'
import {ADD_PRODUCT_HELP as HELP} from './addProductHelpTexts'
import {
  NON_CANONICAL_PRODUCT_TRANSLATION_LANGUAGES,
  NonCanonicalProductTranslationLanguage,
  PRODUCT_TRANSLATION_LANGUAGE_LABELS,
} from './translationLanguages'
import {useTranslation} from 'react-i18next'

const DEFAULT_MAX_RENT_TIME_IN_MINUTES = 360

// Small reusable help icon with hover/focus tooltip (no extra libs).
const HelpTip: React.FC<{text: string; id?: string; className?: string}> = ({text, id, className}) => {
  if (!text || !text.trim()) return null

  return (
    <span className={className} style={{position: 'relative', display: 'inline-block', marginLeft: 8}}>
      <span
        id={id}
        aria-label="Help"
        role="img"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '1px solid currentColor',
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 600,
          cursor: 'default',
          userSelect: 'none',
        }}
      >
        ?
      </span>
      <span
        style={{
          position: 'absolute',
          zIndex: 10,
          top: '100%',
          left: 0,
          marginTop: 6,
          minWidth: 220,
          maxWidth: 360,
          background: 'rgba(0,0,0,0.85)',
          color: '#fff',
          padding: '8px 10px',
          borderRadius: 6,
          fontSize: 12,
          lineHeight: 1.4,
          boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
          opacity: 0,
          pointerEvents: 'none',
          transform: 'translateY(-4px)',
          transition: 'opacity 120ms ease, transform 120ms ease',
          whiteSpace: 'normal',
        }}
        className="help-tip-tooltip"
      >
        {text}
      </span>
      <style>
        {`
          [aria-label="Help"]:hover + .help-tip-tooltip,
          [aria-label="Help"]:focus + .help-tip-tooltip,
          .help-tip-tooltip:hover {
            opacity: 1 !important;
            transform: translateY(0) !important;
            pointer-events: auto !important;
          }
        `}
      </style>
    </span>
  )
}

// Field label paired with HelpTip
const FieldLabel: React.FC<{label: string; help: string}> = ({label, help}) => (
  <Box display="flex" alignItems="center">
    <Typography variant="subtitle2" fontWeight={600}>
      {label}
    </Typography>
    <HelpTip text={help} />
  </Box>
)

interface AddProductProps {}

interface TranslationFormEntry {
  name: string
  description: string
}

// Only the non-canonical languages live in the form's Translations section.
// English is taken from the top-level Name/Description fields on save.
type TranslationsForm = Record<NonCanonicalProductTranslationLanguage, TranslationFormEntry>

interface ProductForm {
  description: string
  imageName: string
  maxRentTimeInMinutes?: number
  name: string
  priceWeight: string
  rentalType: ProductRentalType
  showInApi: boolean
  translationKey: string
  translations: TranslationsForm
}

export const AddProduct: React.FC<AddProductProps> = () => {
  const memoizedInitialForm = useMemo<ProductForm>(() => {
    const translations = NON_CANONICAL_PRODUCT_TRANSLATION_LANGUAGES.reduce(
      (acc, lang) => ({...acc, [lang]: {name: '', description: ''}}),
      {} as TranslationsForm,
    )
    return {
      description: '',
      imageName: '',
      maxRentTimeInMinutes: DEFAULT_MAX_RENT_TIME_IN_MINUTES,
      name: '',
      priceWeight: defaultPriceWeightId,
      rentalType: 'rent',
      showInApi: false,
      translationKey: '',
      translations,
    }
  }, [])
  const {form, updateField} = useForm<ProductForm>(memoizedInitialForm)

  const [addProduct, {loading}] = useAddProductMutation()
  const {showNotification} = useNotification()
  const [savedSuccessfully, setSavedSuccessfully] = useState(false)
  // Tracks whether the user has manually edited imageName. Until then, imageName mirrors translationKey.
  const [imageNameTouched, setImageNameTouched] = useState(false)

  // Default the priceWeight selection to the price weight named "Standard".
  // Done as a one-shot effect because price weights are server-fetched (IDs differ per env), and
  // we don't want a refetch to clobber the user's later selection.
  const {data: priceWeights} = useAllPriceWeights()
  const priceWeightDefaultApplied = useRef(false)
  useEffect(() => {
    if (priceWeightDefaultApplied.current) return
    if (priceWeights.length === 0) return
    const standard = priceWeights.find((pw) => pw.name === 'Standard')
    if (standard) {
      updateField('priceWeight', standard.id)
    }
    priceWeightDefaultApplied.current = true
  }, [priceWeights, updateField])

  const {i18n} = useTranslation()

  const productNameKeys = useMemo(() => {
    try {
      const bundle = i18n.getResourceBundle(i18n.language, 'translation') as any

      const nameNode = bundle?.products?.name

      if (!nameNode || typeof nameNode !== 'object') {
        return [] as string[]
      }

      return Object.keys(nameNode).sort((a, b) => a.localeCompare(b))
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to read products.name keys from i18n bundle', e)
      return [] as string[]
    }
  }, [i18n])

  const onDescriptionChange: TextFieldProps['onChange'] = (value) => {
    updateField('description', value)
  }

  const onImageNameChange: TextFieldProps['onChange'] = (value) => {
    setImageNameTouched(true)
    updateField('imageName', value)
  }

  const onMaxRentTimeInMinutesChange: TextFieldProps['onChange'] = (value) => {
    const valueNumber = parseInt(value, 10)
    if (isNaN(valueNumber)) {
      updateField('maxRentTimeInMinutes', undefined)
    } else {
      updateField('maxRentTimeInMinutes', valueNumber)
    }
  }

  const onNameChange: TextFieldProps['onChange'] = (value) => {
    updateField('name', value)
  }

  const onPriceWeightChange: SelectInputProps<string>['onChange'] = (e) => {
    updateField('priceWeight', e.target.value as string)
  }

  const onRentalTypeChange = (value: ProductRentalType) => {
    updateField('rentalType', value)
  }

  const onShowOnApiChange: SwitchProps['onChange'] = (value) => {
    updateField('showInApi', value)
  }

  const onTranslationKeyChange: TextFieldProps['onChange'] = (value) => {
    updateField('translationKey', value)
    if (!imageNameTouched) {
      updateField('imageName', value)
    }
  }

  const onTranslationFieldChange =
    (lang: NonCanonicalProductTranslationLanguage, field: keyof TranslationFormEntry): TextFieldProps['onChange'] =>
    (value) => {
      updateField('translations', {
        ...form.translations,
        [lang]: {
          ...form.translations[lang],
          [field]: value,
        },
      })
    }

  // Build the GraphQL translations input. English is always present (taken from the top-level Name and
  // Description fields, both required by the form). Non-canonical languages are included only when they
  // have any non-empty content.
  const buildTranslationsInput = (englishName: string, englishDescription: string, translations: TranslationsForm) => {
    const result: Record<string, {name: string | null; description: string | null}> = {
      en: {name: englishName, description: englishDescription},
    }
    for (const lang of NON_CANONICAL_PRODUCT_TRANSLATION_LANGUAGES) {
      const entry = translations[lang]
      const name = entry.name.trim()
      const description = entry.description.trim()
      if (name || description) {
        result[lang] = {
          name: name || null,
          description: description || null,
        }
      }
    }
    return result
  }

  const onSave = async () => {
    const englishName = form.name.trim()
    const englishDescription = form.description.trim()
    if (!englishName || !englishDescription) {
      showNotification({
        message: 'Name and Description are required.',
        severity: 'error',
      })
      return
    }

    try {
      const result = await addProduct({
        variables: {
          addProduct: {
            description: form.description,
            imageName: form.imageName,
            maxRentTimeInMinutes: form.maxRentTimeInMinutes,
            name: form.name,
            priceWeight: form.priceWeight,
            rentalType: transformProductRentalTypeToGQL(form.rentalType),
            showInApi: form.showInApi,
            translationKey: form.translationKey,
            translations: buildTranslationsInput(englishName, englishDescription, form.translations),
          },
        },
        refetchQueries: [{query: AllProductsDocument}],
      })
      console.log('Product added!', result?.data?.addProduct?.product)
      setSavedSuccessfully(true)
    } catch (e) {
      showNotification({
        message: 'Could not add product.',
        severity: 'error',
      })
      console.log('Product add error', e, '--- e')
    }
  }

  if (savedSuccessfully) {
    return (
      <Box display={'flex'} flexDirection={'column'} alignItems={'center'} justifyContent={'center'} width={'100%'} minHeight={'70vh'}>
        <Typography variant={'h4'}>Successfully saved product</Typography>
        <Box mt={2}>
          <Link component={RouterLink} to={Routes.ALL_PRODUCTS}>
            Back to Products
          </Link>
        </Box>
      </Box>
    )
  }

  return (
    <Box display={'inline-flex'} flexDirection={'column'}>
      <Typography variant={'h4'}>Add product</Typography>
      <Typography variant="subtitle1">
        Before you start: Make sure you have prepared translation texts for all languages and also an SVG image for the product.
      </Typography>
      <Typography variant="body1">(See ADDITIONAL INSTRUCTIONS below)</Typography>

      {/* Scoped CSS: hide any internal label inside the wrapped selects */}
      <style>
        {`
          .suppress-internal-select-label label {
            display: none !important;
          }
        `}
      </style>

      <Box mt={3}>
        <FieldLabel label="Name (English)" help={HELP.name} />
        <Box mt={0.5}>
          <TextField id={'add-product-name'} onChange={onNameChange} value={form.name} />
        </Box>
      </Box>

      <Box mt={2}>
        <FieldLabel label="Description (English)" help={HELP.description} />
        <Box mt={0.5}>
          <TextField id={'add-product-description'} onChange={onDescriptionChange} value={form.description} />
        </Box>
      </Box>

      <Box mt={2}>
        <FieldLabel label="Translation key" help={HELP.translationKey + ' SEE LIST BELOW'} />
        <Box mt={0.5}>
          <TextField id={'add-product-translationKey'} onChange={onTranslationKeyChange} value={form.translationKey} />
        </Box>
      </Box>

      <Box mt={3}>
        <Typography variant="subtitle2" fontWeight={600}>
          Translations
        </Typography>
        <Typography variant="body2" sx={{mb: 1, opacity: 0.8}}>
          Translations for additional languages. Optional — missing languages fall back to English (the Name and Description above).
        </Typography>
        {NON_CANONICAL_PRODUCT_TRANSLATION_LANGUAGES.map((lang) => (
          <Box key={lang} mt={1.5} p={1.5} border="1px solid rgba(0,0,0,0.08)" borderRadius={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {PRODUCT_TRANSLATION_LANGUAGE_LABELS[lang]}
            </Typography>
            <Box mt={0.5}>
              <Typography variant="body2">Name</Typography>
              <TextField
                id={`add-product-translation-${lang}-name`}
                value={form.translations[lang].name}
                onChange={onTranslationFieldChange(lang, 'name')}
              />
            </Box>
            <Box mt={1}>
              <Typography variant="body2">Description</Typography>
              <TextField
                id={`add-product-translation-${lang}-description`}
                value={form.translations[lang].description}
                onChange={onTranslationFieldChange(lang, 'description')}
              />
            </Box>
          </Box>
        ))}
      </Box>

      <Box mt={2}>
        <FieldLabel label="Image name" help={HELP.imageName} />
        <Box mt={0.5}>
          <TextField id={'add-product-imageName'} onChange={onImageNameChange} value={form.imageName} />
        </Box>
      </Box>

      <Box mt={2}>
        <FieldLabel label="Max rent time in minutes" help={HELP.maxRentTimeInMinutes} />
        <Box mt={0.5}>
          <TextField id={'add-product-maxRentTimeInMinutes'} onChange={onMaxRentTimeInMinutesChange} value={form.maxRentTimeInMinutes} />
        </Box>
      </Box>

      <Box mt={2}>
        <FieldLabel label="Price weight" help={HELP.priceWeight} />
        <Box mt={0.5} className="suppress-internal-select-label">
          <PriceWeightSelect id={'add-product-priceWeight'} value={form.priceWeight} onChange={onPriceWeightChange} />
        </Box>
      </Box>

      <Box mt={2}>
        <FieldLabel label="Rental type" help={HELP.rentalType} />
        <Box mt={0.5} className="suppress-internal-select-label">
          <RentalTypeSelect id={'add-product-rentalType'} value={form.rentalType} onChange={onRentalTypeChange} />
        </Box>
      </Box>

      <Box mt={2} display="flex" alignItems="center">
        <FieldLabel label="Show in API" help={HELP.showInApi} />
        <Box ml={2}>
          <Switch onChange={onShowOnApiChange} value={form.showInApi} label={undefined as any} />
        </Box>
      </Box>

      <Box mt={3} display={'flex'} alignSelf={'flex-end'}>
        <SaveButton loading={loading} onClick={onSave} />
      </Box>

      {/* Read-only informational notes box */}
      <Box mt={3} p={2} border="1px solid rgba(0,0,0,0.12)" borderRadius={4} bgcolor="rgba(0,0,0,0.02)" maxHeight={240} overflow="auto">
        <Typography
          variant="body2"
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
            lineHeight: 1.5,
          }}
        >
          {`
ADDITIONAL INSTRUCTIONS

Adding a product still requires some developer assistance.
After saving via the form above, please contact the dev team with:
  * the translation key you used
  * the image name you used (usually the same as the translation key)
  * the product image SVG file as an attachment — only if the image is new

------------------------------------------------
Instructions for the dev team:

Translations are captured directly on the product (the form's "Translations" section writes them to the
product's \`translations\` field).

  * piffl-web reads translations from the product row in production — no manual blob/catalog work needed.
  * piffl-app: row-aware code is in place but not yet released. Until the next piffl-app release ships and
    is adopted, end users on piffl-app read from the bundled catalog + db blob. To make this new product
    immediately visible in piffl-app, copy the form's translation values into the database language blob
    (same process as before). Once the row-aware release is widely adopted, that manual blob update only
    matters for users still on older app versions.

Product images (SVG) are served from the shared piffl-assets Vercel Blob store. Since 2026-08-24 no repo
keeps local copies, and NO repo needs a release for a new image — piffl-admin, piffl-web and piffl-portal
build the URL from the image name, and piffl-app falls back to \`api.piffl.se/svgs/products/<name>.svg\`
for any image its build did not bundle. Uploading is all it takes to make an image visible everywhere.

For a new image:
1. Upload it to the store. Heads-up: this is the rough edge right now — there is no longer a repo folder to
   drop the SVG into, and \`yarn upload-product-images\` can no longer regenerate the manifest (it refuses,
   because rebuilding from the folders that are left would drop other repos' override mappings). So:
   add the entry to \`piffl-web/scripts/product-images.manifest.json\` by hand — an \`objects\` entry with md5,
   size and a \`sourcePath\` that resolves, plus the name in \`names\` — then run
   \`yarn upload-product-images --upload --verify\` and commit the manifest.
2. That is it for all four frontends. Nothing to release.
3. Optional, piffl-app only: to move the image into the app bundle (renders instantly, works offline)
   run \`npm run populate-products\` in piffl-app — needs a sibling \`../piffl-web\` checkout — and commit the
   regenerated lock and generated files. This ships with the next app release; the image is already
   visible to users before then, via the fallback above.

Good to know:
  * Store objects are treated as immutable: the CDN caches for 30 days, so changed art should be published
    under a new image name rather than overwritten.
  * Upload before the product is first viewed if you can — the CDN also caches "not found" for a few minutes.
  * The art must fill a square frame, or it renders letterboxed in piffl-app's icon slot.

(See doc/product-translations-context.md and, in piffl-knowledge, wiki/software/product-images.md)
`}
        </Typography>
      </Box>

      {/* Existing i18n translation keys for products.name.* */}
      <Box mt={3} p={2} border="1px solid rgba(0,0,0,0.12)" borderRadius={4} bgcolor="rgba(0,0,0,0.02)" maxHeight={240} overflow="auto">
        <Typography variant="subtitle1" gutterBottom>
          Existing translation keys (products.name.*)
        </Typography>

        {productNameKeys.length === 0 ? (
          <Typography variant="body2">No products.name keys found in the active language bundle.</Typography>
        ) : (
          <>
            <Typography variant="body2" gutterBottom>
              Total: {productNameKeys.length}
            </Typography>
            <ul style={{margin: 0, paddingLeft: 20}}>
              {productNameKeys.map((key) => (
                <li key={key} style={{marginBottom: 4}}>
                  <Typography variant="body2">
                    <strong>{key}</strong>
                  </Typography>
                </li>
              ))}
            </ul>
          </>
        )}
      </Box>
    </Box>
  )
}
